import { Sparkles } from 'lucide-react';
import { useAiBuilderForm } from './useAiBuilderForm';
import { GeneratingOverlay } from './sections/GeneratingOverlay';
import { BudgetDurationSection } from './sections/BudgetDurationSection';
import { WhenWhereSection } from './sections/WhenWhereSection';
import { AudienceVisionSection } from './sections/AudienceVisionSection';
import { AgencyClientSelect } from '@/features/campaigns/components/AgencyClientSelect';
import { useAppSelector } from '@/shared/store/hooks';
import { requiresAgencyClient } from '@/shared/config/roles';
import type { AIBuilderProps } from './AIBuilder.types';

export function AIBuilder(props: AIBuilderProps) {
  const { formData, setFormData } = props;
  const needsClient = requiresAgencyClient(useAppSelector((s) => s.auth.user?.role));
  const {
    errors,
    clearFieldError,
    advanced,
    setAdvanced,
    minStart,
    maxStart,
    currency,
    isGenerating,
    handleSubmit,
  } = useAiBuilderForm(props);

  return (
    <div className="camp-fade-up relative w-full">
      <GeneratingOverlay show={isGenerating} />

      <div className="flex flex-col gap-4">
        <header className="text-left">
          <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
            Tell AI what you need
          </p>
          <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Create your brief
          </h1>
          <p className="mt-1.5 mb-0 max-w-xl text-sm leading-relaxed text-[var(--dash-muted)]">
            Answer a few questions — we&apos;ll pick screens and time slots that
            fit your budget. You can review everything before paying.
          </p>
        </header>

          {needsClient ? (
            <section className="camp-section text-left">
              <AgencyClientSelect
                value={formData.agencyClientId}
                error={errors.agencyClientId}
                onChange={(clientId) => {
                  clearFieldError('agencyClientId');
                  setFormData((prev) => ({ ...prev, agencyClientId: clientId }));
                }}
              />
            </section>
          ) : null}

        <BudgetDurationSection
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          currency={currency}
        />

        <WhenWhereSection
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          minStart={minStart}
          maxStart={maxStart}
        />

        <AudienceVisionSection
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          clearFieldError={clearFieldError}
          advanced={advanced}
          setAdvanced={setAdvanced}
        />

        <button
          type="button"
          disabled={isGenerating}
          onClick={handleSubmit}
          className="dash-btn-gradient sticky bottom-3 z-20 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#145E8C]/20 disabled:opacity-60"
        >
          <Sparkles className="h-4 w-4" />
          Generate AI plan
        </button>
      </div>
    </div>
  );
}
